import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";

export function PromoBannerCarousel({ position = "homepage" }) {
  const [banners, setBanners] = useState([]);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    api.get("/banners", { params: { position } }).then((r) => setBanners(r.data)).catch(() => {});
  }, [position]);

  const next = useCallback(() => setCurrent((c) => (c + 1) % banners.length), [banners.length]);
  const prev = useCallback(() => setCurrent((c) => (c - 1 + banners.length) % banners.length), [banners.length]);

  // Auto-advance every 5s
  useEffect(() => {
    if (banners.length <= 1) return;
    const t = setInterval(next, 5000);
    return () => clearInterval(t);
  }, [banners.length, next]);

  if (banners.length === 0) return null;

  const banner = banners[current];
  const Wrapper = banner.link_url ? Link : "div";
  const wrapperProps = banner.link_url ? { to: banner.link_url } : {};

  return (
    <div className="relative group" data-testid="promo-banner-carousel">
      <Wrapper {...wrapperProps} className="block relative rounded-2xl overflow-hidden aspect-[3/1] sm:aspect-[4/1]">
        <img
          src={banner.image_url}
          alt={banner.title}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-transparent" />
        <div className="absolute bottom-4 left-4 sm:bottom-6 sm:left-6 text-white max-w-md">
          <h3 className="text-lg sm:text-2xl font-bold leading-tight drop-shadow-lg">{banner.title}</h3>
          {banner.subtitle && <p className="text-xs sm:text-sm mt-1 opacity-90 drop-shadow">{banner.subtitle}</p>}
        </div>
      </Wrapper>

      {banners.length > 1 && (
        <>
          <button
            onClick={(e) => { e.preventDefault(); prev(); }}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 hover:bg-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow"
            data-testid="banner-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => { e.preventDefault(); next(); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 hover:bg-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow"
            data-testid="banner-next"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
            {banners.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`w-2 h-2 rounded-full transition-all ${i === current ? "bg-white w-5" : "bg-white/50"}`}
                data-testid={`banner-dot-${i}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
